import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { HorizontalWeldFeature3D } from '../features/HorizontalWeldFeature3D'
import { PlateFeature3D } from '../features/PlateFeature3D'
import { VerticalWeldFeature3D } from '../features/VerticalWeldFeature3D'
import {
  createQuarterTorusGeometry,
  planeGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoHoodBend,
  GeckoHorizontalWeld,
  GeckoPlate,
  GeckoVerticalWeld,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A Rich Asset Model Hood Bend component, plotted on a cartesian plane, with the center of its
 * base at "position" and decorated with Rich Asset features specific to hood bends.
 * @param model               The hood bend component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const HoodBendSurface3D: React.FC<
  { model: GeckoHoodBend } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, verticalWelds, horizontalWelds, plates } =
    React.useMemo(() => {
      const verticalWelds: Array<GeckoVerticalWeld> = model.verticalWeldsArray
      const horizontalWelds: Array<GeckoHorizontalWeld> =
        model.horizontalWeldsArray
      const plates: Array<GeckoPlate> = model.platesArray
      const scale = extractScale(groupProps.scale).multiply(
        show3D
          ? new Vector3(1, 1, 1) // No additional scaling needed in 3D mode
          : new Vector3(
              model.componentBounds.width.num,
              model.componentBounds.height.num,
              1.0
            )
      )

      return {
        bounds: model.componentBounds,
        scale: scale,
        verticalWelds: verticalWelds,
        horizontalWelds: horizontalWelds,
        plates: plates,
      }
    }, [model, show3D, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return createQuarterTorusGeometry(
      model.majorRadius.num, // Distance from center to tube center
      model.minorRadius.num,
      model.torusAngle // Tube radius
    )
  }, [show3D, model.majorRadius.num, model.minorRadius.num, model.torusAngle])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      {verticalWelds.map((weld) => (
        <VerticalWeldFeature3D key={weld.id} model={weld} />
      ))}
      {horizontalWelds.map((weld) => (
        <HorizontalWeldFeature3D key={weld.id} model={weld} />
      ))}
      {plates.map((plate) => (
        <PlateFeature3D key={plate.id} model={plate} />
      ))}
    </ModelSurface3D>
  )
}
